import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { Language } from '@app/components/various/Icons';
import { requestLanguageChange } from '@app/ducks';
import { useAmplitude, useTranslationsConfig } from '@app/utils/hooks';
import { Translations } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

interface Props {
  className?: string;
}

export const LanguageSwitcher = ({ className }: Props) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAmplitude();
  const { t } = useTranslation(['common']);
  const { currentLanguageCode, availableLanguages, areMultipleLanguagesAvailable } = useTranslationsConfig();

  const handleSelect = React.useCallback(
    (lang: Translations.SupportedLanguagesCodes) => {
      dispatch(requestLanguageChange(lang));
      trackEvent({
        name: 'userPreferences.button.language.click',
        properties: {
          deviceLanguage: navigator.language,
          path: window.location.pathname,
          previousLanguage: currentLanguageCode,
          selectedLanguage: lang,
        },
      });
    },
    [currentLanguageCode, dispatch, trackEvent],
  );

  const currentLanguageName = availableLanguages.find(lang => lang.code === currentLanguageCode)?.label;

  const options = React.useMemo(() => {
    return availableLanguages.map(({ code, label }) => ({
      key: code,
      label,
    }));
  }, [availableLanguages]);

  if (!areMultipleLanguagesAvailable) {
    return null;
  }

  return (
    <Dropdown title={t('common:language_one')} placement="bottom-end">
      <DropdownTrigger>
        {({ isOpen, triggerProps }) => (
          <Button
            icon={Language}
            variant="flat"
            color="secondary"
            size="medium"
            className={className}
            aria-label={`${t('common:language_one')} (${currentLanguageName})`}
            isChevronOpen={isOpen}
            {...triggerProps}
          >
            <span className="hidden lg:block">{currentLanguageName}</span>
          </Button>
        )}
      </DropdownTrigger>
      <DropdownMenu
        selectionMode="single"
        selectedKeys={[currentLanguageCode]}
        items={options}
        onSelectionChange={([languageCode]) => handleSelect(languageCode as Translations.SupportedLanguagesCodes)}
      />
    </Dropdown>
  );
};
